<template>
    <div v-if="customer">
        <page-header :title="customer.name">
            <button :class="['button mr-2', {'is-primary': customer.note}]" @click="openCustomerNoteModal()">
                {{ customer.note ? 'Rediger intern note' : 'Opret intern note' }}
            </button>
            <button class="button" @click="openCustomerModal()">
                <span class="icon is-small">
                    <i class="fas fa-edit" />
                </span>
            </button>
        </page-header>
        <hr>

        <div class="columns">
            <div class="column">
                {{ customer.address }}<br />
                {{ customer.zipcode }} {{ customer.city }}<br />
                {{ customer.country }}
            </div>
            <div class="column">
                <div>E-mail: {{ customer.email }}</div>
                <div>Telefon: {{ customer.phonenumber }}</div>
                <div v-if="customer.ean_number">EAN: {{ customer.ean_number }}</div>
                <div v-if="customer.cvr_number">CVR: {{ customer.cvr_number }}</div>
            </div>
        </div>

        <div class="level">
            <div class="level-left">
                <h3 class="is-size-5">Lokationer</h3>
            </div>
            <div class="level-right">
                <button class="button is-primary" @click="openLocationModal()">Opret</button>
            </div>
        </div>

        <list
            v-if="!isFetching"
            table-classes="table is-fullwidth"
            :columns="locationColumns"
            :data="customer.locations"
            :pagination="false"
            :page-length="0"
            :default-order="{ col: 'primary', dir:'desc'}"
            @rowClicked="row => openLocationModal(row)"
        >
            <template slot="column-actions" slot-scope="{ row }">
                <a class="button is-small" @click="openLocationModal(row)" title="Rediger">
                    <span class="icon">
                        <i class="fas fa-edit" />
                    </span>
                </a>
            </template>
        </list>

        <hr class="contacts-hr">

        <div class="level">
            <div class="level-left">
                <h3 class="is-size-5">Kontaktpersoner</h3>
            </div>
            <div class="level-right">
                <button class="button is-primary" @click="openContactModal()">Opret</button>
            </div>
        </div>

        <list
            v-if="!isFetching"
            table-classes="table is-fullwidth"
            :columns="contactColumns"
            :data="customer.contacts"
            :pagination="false"
            :page-length="0"
            @rowClicked="row => openContactModal(row)"
        >
            <template slot="column-actions" slot-scope="{ row }">
                <a class="button is-small" @click="openContactModal(row)" title="Rediger">
                    <span class="icon">
                        <i class="fas fa-edit" />
                    </span>
                </a>
            </template>
        </list>

        <hr>

        <CaseList
            :projects="projects"
            headline="Projekt"
            :customer="customer"
        />

        <CustomerNoteModal
            :active="customerNoteModalOpen"
            :customer="customer"
            @closed="customerNoteModalClosed"
            @updated="customerNoteUpdated"
        />

        <customer-modal
            :active="customerModalOpen"
            :customer="customer"
            @closed="customerModalClosed"
        />

        <contact-modal
            :active="contactModalOpen"
            :customer-id="id"
            :contact="selectedContact"
            @closed="closeContactModal"
        />

        <location-modal
            v-if="!isFetching"
            :active="locationModalOpen"
            :customer-id="id"
            :location="selectedLocation"
            :contacts="customer.contacts"
            @closed="closeLocationModal"
        />
    </div>
</template>

<script>
import PageHeader from '@elems/pageHeader'
import List from '@elems/list'
import CustomerModal from './CustomerModal'
import ContactModal from './ContactModal'
import LocationModal from './LocationModal'
import CaseList from "../projects/CaseList"
import CustomerNoteModal from './CustomerNoteModal'

export default {
    components: {
        PageHeader,
        List,
        CustomerModal,
        ContactModal,
        LocationModal,
        CaseList,
        CustomerNoteModal
    },

    props: {
        id: String,
    },

    data() {
        return {
            locationColumns: [
                { name: 'primary', title: '', hidden: true, sortable: true, searchable: false },
                { name: 'name', title: 'Navn', sortable: true, searchable: true, template: row => row.name + (row.primary ? ' (primær)' : '') },
                { name: 'address', title: 'Adresse', sortable: true, searchable: true },
                { name: 'city', title: 'By', sortable: true, searchable: true },
                { name: 'contacts', title: 'Kontaktpersoner', sortable: false, searchable: true, template: this.getLocationContactsList },
                { name: 'actions', title: '', sortable: false, searchable: false, classes:() => 'has-text-right', width: '100px' },
            ],
            contactColumns: [
                { name: 'name', title: 'Navn', sortable: true, searchable: true },
                { name: 'email', title: 'E-mail', sortable: true, searchable: true },
                { name: 'mobilenumber', title: 'Mobil', sortable: true, searchable: true },
                { name: 'locations', title: 'Lokationer', sortable: false, searchable: true, template: (row) => row.locations.map(l => l.name).join(',<br>') },
                { name: 'actions', title: '', sortable: false, searchable: false, classes:() => 'has-text-right', width: '100px' },
            ],
            customerModalOpen: false,
            customerNoteModalOpen: false,
            contactModalOpen: false,
            locationModalOpen: false,
            selectedContact: null,
            selectedLocation: null,
            customer: {},
            isFetching: true,
        }
    },

    computed: {
        projects() {
            let projects = [];

            if (!this.isFetching) {
                if (this.customer.projects.length > 0) {
                    projects = projects.concat(this.customer.projects);
                }

                if (this.customer.ext_projects.length > 0) {
                    projects = projects.concat(this.customer.ext_projects);
                }
            }

            return projects;
        }
    },

    methods: {
        customerNoteUpdated() {
            this.fetchCustomer();
        },
        openCustomerNoteModal() {
            this.customerNoteModalOpen = true
        },
        customerNoteModalClosed() {
            this.customerNoteModalOpen = false
        },
        openCustomerModal() {
            this.customerModalOpen = true
        },
        openContactModal(contact = null) {
            this.selectedContact = contact
            this.contactModalOpen = true
        },
        openLocationModal(location = null) {
            this.selectedLocation = location
            this.locationModalOpen = true
        },
        customerModalClosed() {
            this.fetchCustomer();
            this.closeCustomerModal()
        },
        closeCustomerModal() {
            this.customerModalOpen = false
        },
        closeContactModal() {
            this.contactModalOpen = false
            this.fetchCustomer();
        },
        closeLocationModal() {
            this.locationModalOpen = false
            this.fetchCustomer();
        },
        getLocationContactsList(row) {
            return row.contacts.map(contact => {
                return contact.name + (contact.pivot.primary ? ' (Primær)' : '')
            }).join(',<br>')
        },
        fetchCustomer() {
            this.isFetching = true;

            window.axios.get('/api/customers/' + this.id)
                .then(({ data }) => {
                    this.customer = data;
                    this.isFetching = false;
                })
                .catch(error => {
                    this.isFetching = true;
                    console.error(error)
                });
        }
    },

    created() {
        this.fetchCustomer();
    }
}
</script>

<style>
.contacts-hr {
    margin-top: 40px;
}
</style>
